import { useState, useMemo, useRef, useEffect, useLayoutEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useOrg } from '@/context/OrgContext'
import { useSelectedTour } from '@/context/TourContext'
import type { RolNivel } from '@/types'
import { Skeleton } from '@/components/ui/skeleton'
import { InviteMemberForm } from '@/components/InviteMemberForm'
import { PendingInvitationsList } from '@/components/PendingInvitationsList'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { Trash2, Users, UserPlus, Loader2, Plus, UsersRound } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import {
  useTourMembers, useTourAssignmentCounts, useAssignableOrgMembers,
  type MemberRow,
} from '@/hooks/useTourCrew'

const ROLE_LABELS: Record<RolNivel, string> = {
  A: 'Manager',
  B: 'Artist',
  C: 'Crew',
}

const ROLE_ORDER: RolNivel[] = ['A', 'B', 'C']

const ROLE_GROUP_LABELS: Record<RolNivel, string> = {
  A: 'Managers',
  B: 'Artists',
  C: 'Crew',
}

const ROLE_GROUP_LABELS_SINGULAR: Record<RolNivel, string> = {
  A: 'Manager',
  B: 'Artist',
  C: 'Crew',
}

export function CrewView() {
  const { activeOrg } = useOrg()
  const { selectedTour } = useSelectedTour()
  const [inviteOpen, setInviteOpen] = useState(false)

  const { data: crew = [], isLoading } = useTourMembers(selectedTour?.id)
  const { data: assignmentCounts } = useTourAssignmentCounts(selectedTour?.id)

  if (!activeOrg) return null

  if (!selectedTour) {
    return (
      <div className="rounded-2xl border border-border bg-card p-5 lg:p-8 text-center">
        <div className="w-12 h-12 rounded-xl bg-muted/40 flex items-center justify-center mb-3 mx-auto">
          <Users className="w-5 h-5 text-muted-foreground" strokeWidth={1.75} />
        </div>
        <p className="text-sm font-medium text-foreground mb-1">No tour selected</p>
        <p className="text-xs text-muted-foreground max-w-xs mx-auto leading-relaxed">
          Pick a tour from the header to see and manage its crew.
        </p>
      </div>
    )
  }

  const memberCount = crew.length
  const groupedByRole = ROLE_ORDER
    .map(role => ({
      role,
      label: ROLE_GROUP_LABELS[role],
      members: crew.filter(m => m.rol_nivel === role),
    }))
    .filter(g => g.members.length > 0)

  const tourMemberIds = new Set(crew.map(m => m.user_id))

  return (
    <>
      {/* ───── Mobile / tablet (<lg): single-card layout ───── */}
      <div className="lg:hidden rounded-2xl border border-border bg-card p-5 space-y-4 animate-in fade-in slide-in-from-bottom-1 dur-base ease-out-soft">
        <div className="flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
            Crew
            {!isLoading && (
              <span className="ml-1.5 text-muted-foreground/70 normal-case tracking-normal font-normal">
                · {memberCount}
              </span>
            )}
          </p>
          {!inviteOpen && (
            <button
              type="button"
              onClick={() => setInviteOpen(true)}
              className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-primary hover:opacity-85 transition-opacity px-2 py-1 -mr-2 rounded-md"
            >
              <UserPlus className="w-3.5 h-3.5" strokeWidth={2.25} />
              Add
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="space-y-1.5">
            <Skeleton className="h-8 rounded-md" />
            <Skeleton className="h-8 rounded-md" />
            <Skeleton className="h-8 rounded-md" />
          </div>
        ) : crew.length === 0 ? (
          <EmptyCrew />
        ) : (
          <ul className="space-y-1">
            {crew.map((member, i) => (
              <li
                key={member.user_id}
                className="animate-in fade-in slide-in-from-bottom-1 dur-base ease-out-soft fill-mode-backwards"
                style={{ animationDelay: `${i * 50}ms` }}
              >
                <MemberRowView
                  member={member}
                  tourId={selectedTour.id}
                  assignmentCount={assignmentCounts?.get(member.user_id) ?? 0}
                />
              </li>
            ))}
          </ul>
        )}

        <PendingInvitationsList orgId={activeOrg.id} tourId={selectedTour.id} />

        <div
          className={cn(
            'grid transition-[grid-template-rows] dur-slow ease-in-out',
            inviteOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
          )}
        >
          <div className="overflow-hidden">
            <div className="space-y-4 py-1 px-1">
              <AssignFromOrgPicker
                orgId={activeOrg.id}
                tourId={selectedTour.id}
                tourMemberIds={tourMemberIds}
              />
              <div className="space-y-2">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                  Invite by email
                </p>
                <InviteMemberForm
                  orgId={activeOrg.id}
                  tourId={selectedTour.id}
                  invalidationKeys={[
                    ['tour-members', selectedTour.id],
                    ['org-members-for-assign', activeOrg.id],
                    ['org-invitations', activeOrg.id, selectedTour.id],
                  ]}
                />
              </div>
              <div className="flex justify-center pt-1">
                <button
                  type="button"
                  onClick={() => setInviteOpen(false)}
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors px-4 py-2 rounded-md"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ───── Desktop (lg+): split 2-column layout ───── */}
      <div className="hidden lg:grid lg:grid-cols-[320px_1fr] lg:gap-8 animate-in fade-in dur-base ease-out-soft">
        <CrewSidebar
          orgId={activeOrg.id}
          tourId={selectedTour.id}
          memberCount={memberCount}
          groupedByRole={groupedByRole}
          tourMemberIds={tourMemberIds}
          inviteOpen={inviteOpen}
          onInviteOpen={() => setInviteOpen(true)}
          onInviteClose={() => setInviteOpen(false)}
          isLoading={isLoading}
        />
        <CrewMain
          orgId={activeOrg.id}
          tourId={selectedTour.id}
          crew={crew}
          groupedByRole={groupedByRole}
          assignmentCounts={assignmentCounts}
          isLoading={isLoading}
        />
      </div>
    </>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Desktop sidebar — unified card surface with count hero + add-member block.
// ────────────────────────────────────────────────────────────────────────────

interface CrewSidebarProps {
  orgId: string
  tourId: string
  memberCount: number
  groupedByRole: { role: RolNivel; label: string; members: MemberRow[] }[]
  tourMemberIds: Set<string>
  inviteOpen: boolean
  onInviteOpen: () => void
  onInviteClose: () => void
  isLoading: boolean
}

function CrewSidebar({
  orgId, tourId, memberCount, groupedByRole, tourMemberIds,
  inviteOpen, onInviteOpen, onInviteClose, isLoading,
}: CrewSidebarProps) {
  return (
    <aside className="sticky top-24 self-start">
      {/* Outer label mirrors the "MANAGERS · N" h3 in CrewMain so the
          sidebar card and the first member card start at the same Y. */}
      <h3 className="flex items-baseline gap-1.5 mb-3">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
          Tour crew
        </span>
        {!isLoading && (
          <span className="text-[11px] text-muted-foreground/70">
            · {memberCount}
          </span>
        )}
      </h3>
      <div className="rounded-2xl border border-border bg-card p-6 space-y-7">
        <div>
          <div className="flex items-baseline gap-2">
            <span className="text-5xl font-semibold tracking-tight text-foreground leading-none">
              {isLoading ? '—' : memberCount}
            </span>
            <span className="text-sm text-muted-foreground">total</span>
          </div>
          {!isLoading && groupedByRole.length > 0 && (
            <div className="mt-3 flex flex-wrap items-baseline gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
              {groupedByRole.map((g, i) => (
                <span key={g.role} className="inline-flex items-baseline gap-1">
                  {i > 0 && <span className="text-muted-foreground/40 mr-1">·</span>}
                  <span className="font-semibold text-foreground">{g.members.length}</span>
                  <span>{(g.members.length === 1 ? ROLE_GROUP_LABELS_SINGULAR[g.role] : g.label).toLowerCase()}</span>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-3">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
            Add crew
          </p>
          {!inviteOpen && (
            <button
              type="button"
              onClick={onInviteOpen}
              className="login-btn accent gap-2 w-full !h-14 !rounded-xl"
              style={{ boxShadow: 'none' }}
            >
              <UserPlus className="w-[18px] h-[18px]" />
              Add member
            </button>
          )}
          <div
            className={cn(
              'grid transition-[grid-template-rows] dur-slow ease-in-out',
              inviteOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
            )}
          >
            <div className="overflow-hidden">
              <div className="space-y-4 py-1 px-1">
                <AssignFromOrgPicker
                  orgId={orgId}
                  tourId={tourId}
                  tourMemberIds={tourMemberIds}
                />
                <div className="space-y-2">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                    Invite by email
                  </p>
                  <InviteMemberForm
                    orgId={orgId}
                    tourId={tourId}
                    invalidationKeys={[
                      ['tour-members', tourId],
                      ['org-members-for-assign', orgId],
                      ['org-invitations', orgId, tourId],
                    ]}
                  />
                </div>
                <div className="flex justify-center pt-1">
                  <button
                    type="button"
                    onClick={onInviteClose}
                    className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors px-4 py-2 rounded-md"
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </aside>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Desktop main — grouped sections, 2-col card grid, pending list at bottom.
// ────────────────────────────────────────────────────────────────────────────

interface CrewMainProps {
  orgId: string
  tourId: string
  crew: MemberRow[]
  groupedByRole: { role: RolNivel; label: string; members: MemberRow[] }[]
  assignmentCounts: Map<string, number> | undefined
  isLoading: boolean
}

function CrewMain({ orgId, tourId, crew, groupedByRole, assignmentCounts, isLoading }: CrewMainProps) {
  if (isLoading) {
    return (
      <main className="grid grid-cols-2 gap-3">
        {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-16 rounded-xl" />)}
      </main>
    )
  }

  if (crew.length === 0) {
    return (
      <main>
        <EmptyCrew />
      </main>
    )
  }

  return (
    <main className="space-y-7">
      {groupedByRole.map(group => (
        <section key={group.role}>
          <h3 className="flex items-baseline gap-1.5 mb-3">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
              {group.label}
            </span>
            <span className="text-[11px] text-muted-foreground/70">
              · {group.members.length}
            </span>
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {group.members.map((member, i) => (
              <div
                key={member.user_id}
                className="animate-in fade-in slide-in-from-bottom-1 dur-base ease-out-soft fill-mode-backwards"
                style={{ animationDelay: `${i * 40}ms` }}
              >
                <MemberCardView
                  member={member}
                  tourId={tourId}
                  assignmentCount={assignmentCounts?.get(member.user_id) ?? 0}
                />
              </div>
            ))}
          </div>
        </section>
      ))}

      <PendingInvitationsList orgId={orgId} tourId={tourId} />
    </main>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Assign-from-org picker: shows org members not yet on the tour as a small
// dropdown with inline role selection. One click adds them via
// assign_tour_member, no email roundtrip needed.
// ────────────────────────────────────────────────────────────────────────────

interface AssignPickerProps {
  orgId: string
  tourId: string
  tourMemberIds: Set<string>
}

export function AssignFromOrgPicker({ orgId, tourId, tourMemberIds }: AssignPickerProps) {
  const queryClient = useQueryClient()
  const [selectedUserId, setSelectedUserId] = useState<string>('')
  const [role, setRole] = useState<RolNivel>('C')

  const { data: available = [], isLoading } = useAssignableOrgMembers(orgId, tourMemberIds)

  const availableSorted = useMemo(() =>
    [...available].sort((a, b) => {
      const an = a.profile?.nombre ?? a.user_id
      const bn = b.profile?.nombre ?? b.user_id
      return an.localeCompare(bn)
    }), [available]
  )

  const assign = useMutation({
    mutationFn: async () => {
      if (!selectedUserId) throw new Error('pick a member')
      const { error } = await supabase.rpc('assign_tour_member', {
        target_tour_id: tourId,
        target_user_id: selectedUserId,
        target_rol_nivel: role,
      })
      if (error) throw error
    },
    onSuccess: () => {
      toast.success('Added to tour')
      setSelectedUserId('')
      queryClient.invalidateQueries({ queryKey: ['tour-members', tourId] })
      queryClient.invalidateQueries({ queryKey: ['org-members-for-assign', orgId] })
    },
    onError: (err: Error) => toast.error(err.message ?? 'Failed to add member'),
  })

  if (!isLoading && availableSorted.length === 0) return null

  return (
    <div className="space-y-2">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
        Assign from team
      </p>
      <Select value={selectedUserId} onValueChange={setSelectedUserId}>
        <SelectTrigger className="w-full !h-11 text-sm">
          <SelectValue placeholder={isLoading ? 'Loading…' : 'Pick a member'} />
        </SelectTrigger>
        <SelectContent className="min-w-[240px] p-1.5">
          {availableSorted.map(m => (
            <SelectItem key={m.user_id} value={m.user_id} className="py-2.5 text-sm">
              {m.profile?.nombre ?? m.user_id.slice(0, 8)}
              <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                {ROLE_LABELS[m.rol_nivel]}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className="grid grid-cols-[1fr_auto] gap-2 items-center">
        <Select value={role} onValueChange={v => setRole(v as RolNivel)}>
          <SelectTrigger className="w-full !h-11 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="min-w-40 p-1.5">
            <SelectItem value="A" className="py-2.5 text-sm">Manager</SelectItem>
            <SelectItem value="B" className="py-2.5 text-sm">Artist</SelectItem>
            <SelectItem value="C" className="py-2.5 text-sm">Crew</SelectItem>
          </SelectContent>
        </Select>
        <button
          type="button"
          onClick={() => assign.mutate()}
          disabled={!selectedUserId || assign.isPending}
          className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-primary hover:opacity-85 transition-opacity disabled:opacity-40 disabled:hover:opacity-40 disabled:cursor-not-allowed px-3 py-2 rounded-md shrink-0"
        >
          {assign.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" strokeWidth={2.25} />}
          Add
        </button>
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Member card (desktop).
// ────────────────────────────────────────────────────────────────────────────

function MemberCardView({
  member, tourId, assignmentCount,
}: {
  member: MemberRow
  tourId: string
  assignmentCount: number
}) {
  const queryClient = useQueryClient()
  const [confirming, setConfirming] = useState(false)

  const remove = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc('remove_tour_member', {
        target_tour_id: tourId,
        target_user_id: member.user_id,
      })
      if (error) throw error
    },
    onSuccess: () => {
      toast.success('Removed from tour')
      queryClient.invalidateQueries({ queryKey: ['tour-members', tourId] })
      queryClient.invalidateQueries({ queryKey: ['tour-assignment-counts', tourId] })
      queryClient.invalidateQueries({ queryKey: ['org-members-for-assign'] })
      setConfirming(false)
    },
    onError: () => toast.error('Failed to remove member'),
  })

  const name = member.profile?.nombre

  // Flip mechanics: front and back are absolutely positioned within the
  // inner rotator, so they don't stretch each other. The OUTER container's
  // height animates between the two faces' natural heights, which we
  // measure via refs after layout. This keeps the card its natural size
  // when idle (not an inflated size to fit the confirm panel) and grows
  // smoothly when the flip starts.
  const frontRef = useRef<HTMLDivElement>(null)
  const backRef = useRef<HTMLDivElement>(null)
  const [heights, setHeights] = useState({ front: 0, back: 0 })
  const [measured, setMeasured] = useState(false)

  useLayoutEffect(() => {
    if (!frontRef.current || !backRef.current) return
    setHeights({
      front: frontRef.current.scrollHeight,
      back: backRef.current.scrollHeight,
    })
    setMeasured(true)
  }, [assignmentCount, name, remove.isPending])

  // Keep the back face OUT of the visual tree when not confirming.
  // Otherwise view transitions (tab switch) and initial mount can leak
  // the red destructive panel for a frame because snapshot captures
  // don't preserve backface-visibility correctly. We still need the
  // back rendered for measurement — `visibility: hidden` keeps layout
  // intact while removing it from paint. Delay hiding after a cancel
  // flip by 500ms so the rotation animation can finish showing the back.
  const [backHidden, setBackHidden] = useState(true)
  useEffect(() => {
    if (confirming) {
      setBackHidden(false)
      return
    }
    const t = setTimeout(() => setBackHidden(true), 500)
    return () => clearTimeout(t)
  }, [confirming])

  const activeHeight = confirming ? heights.back : heights.front

  return (
    <div
      style={{
        perspective: '1200px',
        height: measured ? `${activeHeight}px` : undefined,
        transition: measured ? 'height 500ms var(--ease-out-soft)' : undefined,
      }}
    >
      <div
        className="relative w-full h-full"
        style={{
          transformStyle: 'preserve-3d',
          transform: confirming ? 'rotateY(180deg)' : 'rotateY(0deg)',
          transition: 'transform 500ms var(--ease-out-soft)',
        }}
      >
        {/* Front — member card, absolutely positioned so its height is
            natural (not stretched to match the back). */}
        <div
          ref={frontRef}
          className={cn(
            'absolute inset-x-0 top-0 rounded-xl border border-border bg-card px-4 py-3 flex items-center gap-3 group hover:border-border-strong transition-colors',
            confirming && 'pointer-events-none'
          )}
          style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
        >
          <MemberAvatar url={member.profile?.avatar_url ?? null} name={name ?? '?'} size="lg" />
          <span className="flex-1 min-w-0 text-sm font-medium text-foreground truncate">
            {name ?? (
              <span className="font-mono text-xs text-muted-foreground">
                {member.user_id.slice(0, 8)}
              </span>
            )}
          </span>
          <button
            onClick={() => setConfirming(true)}
            className="icon-btn !w-7 !h-7 shrink-0 hover:!text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
            aria-label="Remove from tour"
            title="Remove from tour"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Back — destructive confirm panel. Pre-rotated 180 so it faces
            forward when the parent reaches rotateY(180). */}
        <div
          ref={backRef}
          className={cn(
            'absolute inset-x-0 top-0 rounded-xl border border-destructive/25 bg-destructive/[0.04] px-4 py-3.5 space-y-2',
            !confirming && 'pointer-events-none'
          )}
          style={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            visibility: backHidden ? 'hidden' : 'visible',
          }}
        >
          <p className="text-sm text-foreground leading-snug">
            Remove <strong>{name ?? 'this member'}</strong> from the tour?
          </p>
          {assignmentCount > 0 && (
            <p className="text-xs text-muted-foreground leading-relaxed">
              Also unassigns them from {assignmentCount} {assignmentCount === 1 ? 'activity' : 'activities'} in this tour. Their org membership stays.
            </p>
          )}
          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="text-xs font-medium text-muted-foreground hover:text-foreground px-2.5 py-1.5 rounded-md"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
              className="inline-flex items-center gap-1 text-xs font-semibold text-destructive border border-destructive/40 rounded-md px-3 py-1.5 hover:bg-destructive/10 hover:border-destructive/60 disabled:opacity-50 transition-colors"
            >
              {remove.isPending && <Loader2 className="w-3 h-3 animate-spin" />}
              Remove
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Member row (mobile).
// ────────────────────────────────────────────────────────────────────────────

function MemberRowView({
  member, tourId, assignmentCount,
}: {
  member: MemberRow
  tourId: string
  assignmentCount: number
}) {
  const queryClient = useQueryClient()
  const [confirming, setConfirming] = useState(false)

  const remove = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc('remove_tour_member', {
        target_tour_id: tourId,
        target_user_id: member.user_id,
      })
      if (error) throw error
    },
    onSuccess: () => {
      toast.success('Removed from tour')
      queryClient.invalidateQueries({ queryKey: ['tour-members', tourId] })
      queryClient.invalidateQueries({ queryKey: ['tour-assignment-counts', tourId] })
      queryClient.invalidateQueries({ queryKey: ['org-members-for-assign'] })
      setConfirming(false)
    },
    onError: () => toast.error('Failed to remove member'),
  })

  const name = member.profile?.nombre
  const role = ROLE_LABELS[member.rol_nivel]

  if (confirming) {
    return (
      <div className="rounded-xl border border-destructive/25 bg-destructive/[0.04] p-3 space-y-2">
        <p className="text-sm text-foreground leading-snug">
          Remove <strong>{name ?? 'this member'}</strong> from the tour?
        </p>
        {assignmentCount > 0 && (
          <p className="text-xs text-muted-foreground leading-relaxed">
            Also unassigns them from {assignmentCount} {assignmentCount === 1 ? 'activity' : 'activities'} in this tour. Their org membership stays.
          </p>
        )}
        <div className="flex items-center gap-2 pt-1">
          <button
            type="button"
            onClick={() => setConfirming(false)}
            className="text-xs font-medium text-muted-foreground hover:text-foreground px-2.5 py-1.5 rounded-md"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => remove.mutate()}
            disabled={remove.isPending}
            className="inline-flex items-center gap-1 text-xs font-semibold text-destructive border border-destructive/40 rounded-md px-3 py-1.5 hover:bg-destructive/10 hover:border-destructive/60 disabled:opacity-50 transition-colors"
          >
            {remove.isPending && <Loader2 className="w-3 h-3 animate-spin" />}
            Remove
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-3 py-1.5 group">
      <MemberAvatar url={member.profile?.avatar_url ?? null} name={name ?? '?'} />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-foreground truncate">
          {name ?? (
            <span className="font-mono text-xs text-muted-foreground">
              {member.user_id.slice(0, 8)}
            </span>
          )}
        </p>
      </div>
      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground shrink-0">
        {role}
      </span>
      <button
        onClick={() => setConfirming(true)}
        className="icon-btn !w-6 !h-6 shrink-0 hover:!text-destructive sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
        aria-label="Remove from tour"
      >
        <Trash2 className="w-3 h-3" />
      </button>
    </div>
  )
}

function MemberAvatar({ url, name, size = 'md' }: {
  url: string | null
  name: string
  size?: 'md' | 'lg'
}) {
  const dim = size === 'lg' ? 'w-10 h-10 text-sm' : 'w-8 h-8 text-xs'
  if (url) {
    return <img src={url} alt={name} className={cn(dim, 'rounded-full object-cover shrink-0')} />
  }
  return (
    <div className={cn(dim, 'rounded-full bg-muted flex items-center justify-center font-semibold text-muted-foreground shrink-0')}>
      {name.slice(0, 1).toUpperCase()}
    </div>
  )
}

function EmptyCrew() {
  return (
    <div className="flex flex-col items-center justify-center py-10 lg:py-14 text-center">
      <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-xl bg-muted/40 flex items-center justify-center mb-3">
        <UsersRound className="w-5 h-5 lg:w-6 lg:h-6 text-muted-foreground" strokeWidth={1.75} />
      </div>
      <p className="text-sm font-medium text-foreground mb-1">No crew on this tour yet</p>
      <p className="text-xs text-muted-foreground max-w-xs leading-relaxed">
        Assign existing team members or invite someone new with the button above.
      </p>
    </div>
  )
}
